import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const plans = [
  {
    name: "Starter",
    price: "$4",
    unit: "per user / month",
    desc: "For small teams getting HR right from day one.",
    cta: "Start Free Trial",
    intent: "contact_sales",
    features: [
      "Up to 50 employees",
      "Core HR & employee database",
      "Attendance & leave management",
      "Mobile app access",
      "Self-service portal",
      "5 built-in reports",
      "Email support",
    ],
  },
  {
    name: "Professional",
    price: "$8",
    unit: "per user / month",
    desc: "For growing companies that need automation and analytics.",
    cta: "Book a Demo",
    intent: "book_demo",
    featured: true,
    features: [
      "Everything in Starter, plus:",
      "Unlimited employees",
      "Full payroll module",
      "All 13 built-in reports",
      "Advanced approval workflows",
      "Biometric device integrations",
      "Custom letter templates",
      "Priority support",
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    unit: "tailored to you",
    desc: "For large teams with unique workflows and integrations.",
    cta: "Contact Sales",
    intent: "contact_sales",
    features: [
      "Everything in Professional, plus:",
      "Unlimited users & branches",
      "Custom development",
      "SSO / SAML authentication",
      "Custom integrations & APIs",
      "Advanced reporting & analytics",
      "Dedicated success manager",
      "24/7 premium support",
    ],
  },
];

export default function Pricing() {
  return (
    <div data-testid="page-pricing">
      <section className="py-20 md:py-28 hero-grid-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-4">Pricing</div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-[#1F1E4E]">Simple, transparent pricing.</h1>
          <p className="mt-5 text-lg text-slate-600">Pay only for active employees. Cancel anytime.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p) => (
              <div
                key={p.name}
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`rounded-3xl p-8 border transition-all ${
                  p.featured
                    ? "bg-[#1F1E4E] text-white border-[#1F1E4E] shadow-2xl md:scale-105"
                    : "bg-white border-slate-200 hover:-translate-y-1 hover:shadow-md"
                }`}
              >
                {p.featured && (
                  <div className="inline-block bg-[#00A651] text-white text-xs font-bold uppercase tracking-widest rounded-full px-3 py-1 mb-4">
                    Most Popular
                  </div>
                )}
                <div className={`text-lg font-extrabold ${p.featured ? "text-white" : "text-[#1F1E4E]"}`}>{p.name}</div>
                <div className={`mt-4 text-4xl font-extrabold ${p.featured ? "text-white" : "text-[#1F1E4E]"}`}>{p.price}</div>
                <div className={`text-sm ${p.featured ? "text-white/60" : "text-slate-500"}`}>{p.unit}</div>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-white/80" : "text-slate-600"}`}>{p.desc}</p>

                <Link
                  to={`/contact?intent=${p.intent}&plan=${p.name.toLowerCase()}`}
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  className={`mt-6 block text-center font-bold rounded-lg px-6 py-3 transition-colors ${
                    p.featured
                      ? "bg-[#00A651] hover:bg-[#008A43] text-white"
                      : "bg-[#1F1E4E] hover:bg-[#2A2960] text-white"
                  }`}
                >
                  {p.cta}
                </Link>

                <ul className="mt-8 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex gap-2.5">
                      <Check className={`h-4.5 w-4.5 shrink-0 mt-0.5 ${p.featured ? "text-[#00A651]" : "text-[#00A651]"}`} />
                      <span className={`text-sm ${p.featured ? "text-white/90" : "text-slate-700"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-2xl bg-white border border-slate-200 p-8 text-center">
            <h3 className="text-2xl font-extrabold text-[#1F1E4E]">Not sure which plan fits?</h3>
            <p className="mt-2 text-slate-600">Chat with our HR experts — we'll recommend a plan based on your workforce.</p>
            <Link to="/contact?intent=contact_sales" data-testid="pricing-talk-expert" className="inline-flex mt-5 bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3">
              Talk to an HR Expert
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
